import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useRefetchableFragment } from 'react-relay';

import Badge from '@atlaskit/badge/badge';
import { getDocument } from '@atlaskit/browser-apis';
import { useInterval } from '@atlaskit/frontend-utilities/useInterval';
import {
	MenuListItem,
	Notifications as NotificationsTrigger,
} from '@atlaskit/navigation-system/top-nav-items';
import type { TriggerProps as PopupTriggerRenderProps } from '@atlaskit/popup/types';
import type { AnyEntryPoint } from '@atlassian/entry-point-types/types';
import { PopupTrigger, type PopupTriggerProps } from '@atlassian/entry-points/popup-trigger';
import { globalNotificationsPanelEntryPoint } from '@atlassian/notification-list/global-notifications-panel-entrypoint';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import type { NotificationsProps } from '../../common/types';
import type { Notifications$key } from './__generated__/Notifications.graphql';
import type { NotificationsRefetchQuery } from './__generated__/NotificationsRefetchQuery.graphql';
import { messages } from './messages';

const MAX_NOTIFICATION_COUNT = 9;
const NOTIFICATIONS_POLL_INTERVAL = 300000;

type NotificationsWithDataProps<TEntryPoint extends AnyEntryPoint = AnyEntryPoint> =
	NotificationsProps<TEntryPoint> & {
		queryRef?: Notifications$key | null;
	};

const NotificationCountContext = createContext(0);

function NotificationBadge(): JSX.Element | null {
	const count = useContext(NotificationCountContext);

	if (count <= 0) {
		return null;
	}

	return (
		<Badge appearance="important" max={MAX_NOTIFICATION_COUNT}>
			{count}
		</Badge>
	);
}

/**
 * __Notifications__
 *
 * The notifications item in the top navigation.
 *
 * This is the platform default handed to `NotificationsSlot`. It follows the
 * same two-part pattern as `Settings`:
 *
 *  - The trigger is rendered eagerly with a translated label and an optional
 *    unread-count badge, using ADS's `Notifications` top-nav-item primitive.
 *  - The panel content comes from an entry point. The entry-point trigger
 *    preloads it on hover and pointer-down, so the content is usually ready by
 *    the time the click lands.
 *
 * Apps can supply their own panel entry point, or configure the platform-owned
 * shared notification list with `notificationListProps`. Popup wiring, loading
 * and error states, count polling, and preload plumbing stay platform-owned.
 */
export function Notifications<TEntryPoint extends AnyEntryPoint>({
	errorFallback,
	fallback,
	label,
	onClick,
	onOpen,
	queryRef,
	testId,
	unreadCount,
	...panelProps
}: NotificationsWithDataProps<TEntryPoint>): JSX.Element {
	const { formatMessage } = useIntl();
	const { fireEvent } = useAnalyticsEvents();

	// No local @argumentDefinitions: `collabContextRoutingAri` is a query-level
	// variable already declared wherever `queryRef` originates (see
	// `createUseGlobalRootRelayQuery` / AppChrome). Notifications is a default
	// slot, so callers must pass a string ARI — it is not optional.
	const [notificationData, refetchNotifications] = useRefetchableFragment<
		NotificationsRefetchQuery,
		Notifications$key
	>(
		graphql`
			fragment Notifications on Query @refetchable(queryName: "NotificationsRefetchQuery") {
				notifications {
					unseenNotificationCount(collabContextRoutingAri: $collabContextRoutingAri)
				}
			}
		`,
		queryRef ?? null,
	);

	const fetchedUnreadCount = notificationData?.notifications?.unseenNotificationCount ?? 0;
	const sourceUnreadCount = queryRef ? fetchedUnreadCount : (unreadCount ?? 0);
	const [isNotificationCleared, setIsNotificationCleared] = useState(false);

	useEffect(() => {
		if (sourceUnreadCount > 0) {
			setIsNotificationCleared(false);
		}
	}, [sourceUnreadCount]);

	const displayedUnreadCount = isNotificationCleared ? 0 : sourceUnreadCount;
	const resolvedLabel =
		label ??
		(displayedUnreadCount <= MAX_NOTIFICATION_COUNT
			? formatMessage(messages.notificationsWithCount, { count: displayedUnreadCount })
			: formatMessage(messages.overNineNotifications));

	const pollUnreadCount = useCallback(() => {
		if (!queryRef || getDocument()?.hidden) {
			return;
		}

		refetchNotifications({}, { fetchPolicy: 'network-only' });
	}, [queryRef, refetchNotifications]);

	useInterval(pollUnreadCount, queryRef ? NOTIFICATIONS_POLL_INTERVAL : null);

	const handleOpen = useCallback(() => {
		setIsNotificationCleared(true);
		onOpen?.();
	}, [onOpen]);

	const renderPanelTrigger = useCallback(
		({ ref, ...triggerProps }: PopupTriggerRenderProps) => (
			<NotificationsTrigger
				{...triggerProps}
				ref={ref}
				label={resolvedLabel}
				testId={testId}
				badge={NotificationBadge}
				onClick={(event) => {
					fireEvent('ui.topNav.clicked.notifications', {});
					if (!triggerProps['aria-expanded']) {
						handleOpen();
					}
					onClick?.(event);
				}}
				isListItem={false}
			/>
		),
		[fireEvent, handleOpen, onClick, resolvedLabel, testId],
	);

	// NotificationsProps validates each entry point against its matching params and runtime props.
	// Erase that generic relationship only after resolving the branch so one PopupTrigger can render it.
	const { entryPoint, entryPointParams, entryPointProps } = (
		'notificationListProps' in panelProps
			? {
					entryPoint: globalNotificationsPanelEntryPoint,
					entryPointParams: {},
					entryPointProps: {
						NotificationListContainer: panelProps.NotificationListContainer,
						notificationListProps: panelProps.notificationListProps,
					},
				}
			: panelProps
	) as Pick<
		PopupTriggerProps<AnyEntryPoint>,
		'entryPoint' | 'entryPointParams' | 'entryPointProps'
	>;

	// Notifications is a large asynchronous panel rather than a menu. PopupTrigger
	// asks Popper to reposition when its loading fallback is replaced, preventing
	// the first-loaded panel from retaining the fallback's narrower placement.
	return (
		<NotificationCountContext.Provider value={displayedUnreadCount}>
			<MenuListItem>
				<PopupTrigger
					entryPoint={entryPoint}
					entryPointParams={entryPointParams}
					entryPointProps={entryPointProps}
					errorFallback={errorFallback}
					fallback={fallback}
					label={resolvedLabel}
					placement="bottom-end"
					shouldRenderToParent
					testId={testId}
					trigger={renderPanelTrigger}
				/>
			</MenuListItem>
		</NotificationCountContext.Provider>
	);
}
